import styled from "@emotion/styled"
import Image from "components/media/ImageCLD"
import Video, { cssContainer, cssFallback, cssPoster, cssVideo } from "components/media/VideoCLD"
import { cssVars } from "components/nav-v2/Wrapper"
import ScrollInvit from "components/scroll-invit/ScrollInvit"
import { fullGrid } from "css/grid"
import { motion, useMotionValueEvent, useReducedMotion, useScroll, useTransform } from "framer-motion"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import TextBlock from "./TextBlock"
import Roller from "./roller/Roller"

const ENTER_SCROLL_VH = 45
const INSTALLATION_RESET_SCROLL_VH = 35
const FADE_START_SCROLL_VH = 5
const FADE_END_SCROLL_VH = 45

const Root = styled.div`
  ${fullGrid}
  height: 200vh;
  margin-top: calc((var(${cssVars.main.safe}) - 1px) * -1);
  position: relative;
`

const MediaContainer = styled.div`
  grid-column: doc;
  grid-row: 1;
  height: calc(100vh + 2px);
  overflow: hidden;
  position: sticky;
  top: 0;
  width: 100%;

  .${cssContainer} {
    height: 100%;
    width: 100%;
  }

  picture,
  img,
  .${cssVideo}, .${cssPoster}, .${cssFallback} {
    display: block;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }
`

const Overlay = styled(motion.div)`
  background: rgb(0 0 0 / 0.8);
  backdrop-filter: blur(5px);
  inset: 0;
  pointer-events: none;
  position: absolute;
  z-index: 1;
`

const RollerContainer = styled.div`
  align-content: center;
  display: grid;
  grid-column: doc;
  grid-row: 1;
  height: 100vh;
  overflow: hidden;
  position: sticky;
  top: 0;
  z-index: 2;
`

const RollerFrame = styled.div`
  display: grid;
  height: 100%;
  margin-inline: auto;
  width: min(100%, 1920px);
`

let _timeout
export default function HeroMediaOverlay({ theme, heading, cta, media, items, use_os_logo, animated_logo, logo_translation }) {
  const rroot = useRef()
  const rroller = useRef()
  const raction = useRef()
  const rvideo = useRef()
  const pausedByInstallationRef = useRef(false)
  const reducedMotion = useReducedMotion()

  const [isTransitionDone, setTransitionDone] = useState(false)
  const [isRollerFocusWithin, setRollerFocusWithin] = useState(false)
  const [thresholds, setThresholds] = useState({ enter: 0.12, installationReset: 0.08, fadeStart: 0.04, fadeEnd: 0.18 })
  const [ready, setReady] = useState(false)

  const thresholdsRef = useRef(thresholds)

  const { video, image } = useMemo(() => {
    const video = media?.find(m => m?.video_cld)?.video_cld ?? null
    const image = video ? null : media?.find(m => m?.image_cld)?.image_cld ?? null
    return { video, image }
  }, [media])

  const prefersReducedMotion = () => process.browser && document.documentElement.classList.contains("prefers-reduced-motion")

  useEffect(() => {
    const root = rroot.current
    if (!root) return
    const candidates = Array.from(root.querySelectorAll(".textblock a[href], .textblock button"))
    raction.current = candidates.find(el => !el.closest(".video-player-content, .cld-video-player")) ?? null
  }, [])

  const onMediaClick = useCallback(e => {
    if (e.target.closest("a, button")) return
    raction.current?.click()
  }, [])

  const { scrollYProgress } = useScroll({
    target: rroot,
    offset: ["start start", "end start"],
  })
  const overlayOpacity = useTransform(scrollYProgress, [0, thresholds.fadeStart, thresholds.fadeEnd, 1], [0, 0, 1, 1])
  const textOpacity = useTransform(scrollYProgress, [0, thresholds.fadeStart, thresholds.fadeEnd, 1], [1, 1, 0, 0])

  const clearRollerFocus = useCallback(() => {
    setRollerFocusWithin(false)

    const roller = rroller.current
    const active = document.activeElement
    if (roller?.contains(active)) active?.blur?.()
  }, [])

  useEffect(() => {
    const root = rroot.current
    if (!root || typeof window === "undefined") return

    if (reducedMotion) {
      setTransitionDone(false)
      return
    }

    const compute = () => {
      const vhPx = window.innerHeight / 100
      const enterPx = ENTER_SCROLL_VH * vhPx
      const installationResetPx = INSTALLATION_RESET_SCROLL_VH * vhPx
      const fadeStartPx = FADE_START_SCROLL_VH * vhPx
      const fadeEndPx = FADE_END_SCROLL_VH * vhPx

      const h = root.getBoundingClientRect().height || 1

      const nextThresholds = {
        enter: Math.min(1, Math.max(0, enterPx / h)),
        installationReset: Math.min(1, Math.max(0, installationResetPx / h)),
        fadeStart: Math.min(1, Math.max(0, fadeStartPx / h)),
        fadeEnd: Math.min(1, Math.max(0, fadeEndPx / h)),
      }

      thresholdsRef.current = nextThresholds
      setThresholds(nextThresholds)
    }

    compute()

    const observer = new ResizeObserver(compute)
    observer.observe(root)
    window.addEventListener("resize", compute, { passive: true })

    return () => {
      observer.disconnect()
      window.removeEventListener("resize", compute)
    }
  }, [reducedMotion])

  useMotionValueEvent(scrollYProgress, "change", p => {
    if (reducedMotion) return
    const { enter, installationReset } = thresholdsRef.current
    const previous = scrollYProgress.getPrevious()

    if (p <= installationReset && p < previous) clearRollerFocus()

    setTransitionDone(prev => {
      if (!prev && p >= enter) return true
      if (prev && p <= installationReset) return false
      return prev
    })
  })

  const installationVisible = isTransitionDone || isRollerFocusWithin
  const themeClass = theme?.theme ?? "dark-theme"

  const onRollerFocusCapture = useCallback(() => {
    setRollerFocusWithin(true)
  }, [])

  const onRollerBlurCapture = useCallback(event => {
    if (event.currentTarget.contains(event.relatedTarget)) return
    setRollerFocusWithin(false)
  }, [])

  useEffect(() => {
    const ref = rvideo.current
    if (!ref) return

    if (installationVisible) {
      if (ref.isPlaying) {
        ref.pause({ keepPlayAttribute: true })
        pausedByInstallationRef.current = true
      }
      return
    }

    if (pausedByInstallationRef.current) {
      ref.play()
      pausedByInstallationRef.current = false
    }
  }, [installationVisible])

  useEffect(() => {
    if (!ready) {
      if (_timeout) clearTimeout(_timeout)

      _timeout = setTimeout(() => {
        setReady(true)
      }, 100)
    }
    if (ready) {
      requestAnimationFrame(() => {
        if (!prefersReducedMotion() && !rvideo.current.node.playing) rvideo.current.node.play()
      })
    }
  }, [ready])

  return (
    <Root ref={rroot} className={themeClass}>
      {ready && (video || image) && (
        <MediaContainer onClick={onMediaClick}>
          {video ? (
            <Video ref={rvideo} {...video} noButton autoPause={false} sizes='100vw' preload='auto' />
          ) : (
            <Image {...image} sizes='100vw' loading='eager' />
          )}

          <Overlay style={{ opacity: reducedMotion ? (installationVisible ? 1 : 0) : isRollerFocusWithin ? 1 : overlayOpacity }} />
        </MediaContainer>
      )}

      <TextBlock
        heading={heading}
        cta={cta}
        opacity={reducedMotion ? (installationVisible ? 0 : 1) : isRollerFocusWithin ? 0 : textOpacity}
        isInteractive={!installationVisible}
        use_os_logo={use_os_logo}
        animated_logo={animated_logo}
        logo_translation={logo_translation}
      />

      <RollerContainer
        ref={rroller}
        style={{ pointerEvents: installationVisible ? "auto" : "none" }}
        onFocusCapture={onRollerFocusCapture}
        onBlurCapture={onRollerBlurCapture}
      >
        <RollerFrame>
          <Roller column_width='y-col-roller' items={items} installationVisible={installationVisible} />
        </RollerFrame>
      </RollerContainer>
      <ScrollInvit />
    </Root>
  )
}
